var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { CustomElement, P, CustomElementUtils, Utils, Logging, CustomEventUtils, UI, avoidHandler } from '@pacem/pacem-core';
import { Matrix2D, Rect } from '@pacem/pacem-foundation';
import { UiElement, ShapeElement } from '../types';
import { TAG_MIDDLE_NAME } from '../constants';
import { Pacem2DAdapterElement } from '../adapter';
import { getStageOptions } from '../stage';
import { AdapterUtils } from './utils';
import { PacemGroupElement } from '../group';
import { PacemImageElement } from '../image';
import { PacemTextElement } from '../text';
import { PresentationState, isDrawable, isUiObject, isPresentationObject, isShape, isText, isImage, isGroup, isLinearGradient, isRadialGradient, DrawableEvent } from '../drawing';
//namespace Pacem.Components.Drawing {
const CANVAS_SCENE_VAR = 'pacem:2d-canvas-scene';
const PARENT_MATRIX_VAR = 'pacem:2d-parent-matrix';
const WORLD_MATRIX_VAR = 'pacem:2d-world-matrix';
const SETVAR = CustomElementUtils.setAttachedPropertyValue, GETVAR = CustomElementUtils.getAttachedPropertyValue;
function isNone(fillOrStroke) {
    return fillOrStroke === 'none' || Utils.isNullOrEmpty(fillOrStroke);
}
function fallback(v, f) {
    return Utils.isNull(v) ? f : v;
}
/** Implementation postponed. Focus on SVG adapter. */
let PacemCanvasAdapterElement = class PacemCanvasAdapterElement extends Pacem2DAdapterElement {
    constructor() {
        super(...arguments);
        this._pointer = {
            page: { x: Number.NaN, y: Number.NaN }, screen: { x: Number.NaN, y: Number.NaN }, client: { x: Number.NaN, y: Number.NaN }
        };
        this._dragInitHandler = (evt) => {
            const hitTarget = this._hitTarget;
            if (Utils.isNull(hitTarget) || !isUiObject(hitTarget) || !hitTarget.draggable) {
                evt.preventDefault();
                return;
            }
            const args = evt.detail, drawable = hitTarget, initialMatrix = hitTarget.transformMatrix ?? Matrix2D.identity, parentMatrix = GETVAR(hitTarget, PARENT_MATRIX_VAR, Matrix2D.identity);
            args.data = {
                item: drawable,
                initialTransformMatrix: initialMatrix,
                parentMatrix
            };
            const reject = AdapterUtils.itemDispatch(drawable, evt, { x: 0, y: 0 });
            if (reject) {
                // reject dragging
                evt.preventDefault();
            }
        };
        this._draggingHandler = (evt) => {
            avoidHandler(evt);
            this._dragging = true;
            const args = evt.detail;
            const data = args.data;
            const screenOffset = { x: args.currentPosition.x - args.origin.x, y: args.currentPosition.y - args.origin.y };
            const offset = {
                x: screenOffset.x * data.parentMatrix.a + data.initialTransformMatrix.e,
                y: screenOffset.y * data.parentMatrix.d + data.initialTransformMatrix.f
            };
            //console.log(`current pos: ${args.currentPosition.x},${args.currentPosition.y}`);
            //console.log(`origin pos: ${args.origin.x},${args.origin.y}`);
            //console.log(`screen offset: ${screenOffset.x},${screenOffset.y}`);
            //console.log(`offset: ${offset.x},${offset.y}`);
            //console.log(data.item.transformMatrix);
            //console.log(GETVAR(data.item, PARENT_MATRIX_VAR));
            //console.log(GETVAR(data.item, WORLD_MATRIX_VAR));
            //console.log(data.initialTransformMatrix);
            const stageTransformMatrix = data.item.stage.transformMatrix;
            const rejected = AdapterUtils.itemDispatch(data.item, evt, { x: screenOffset.x * stageTransformMatrix.a + stageTransformMatrix.e, y: screenOffset.y * stageTransformMatrix.d + stageTransformMatrix.f });
            if (!rejected) {
                if (data.item instanceof UiElement) {
                    data.item.translateX = offset.x;
                    data.item.translateY = offset.y;
                }
                else {
                    const init = data.initialTransformMatrix, actual = { a: init.a, b: init.b, c: init.c, d: init.d, e: offset.x, f: offset.y };
                    Utils.extend(data.item, { transformMatrix: actual });
                }
                // console.log(data.item.transformMatrix);
            }
        };
        this._dragEndHandler = (evt) => {
            this._dragging = false;
            const args = evt.detail, data = args.data, transform = data.item.transformMatrix;
            AdapterUtils.itemDispatch(data.item, evt, { x: transform.e, y: transform.f });
        };
        // #endregion
        this._mouseleaveHandler = (evt) => {
            if (this._dragging) {
                return;
            }
            const hitTarget = this._hitTarget;
            if (Utils.isNull(hitTarget)) {
                return;
            }
            this._scopeEvent = evt;
            const nap = { x: Number.NaN, y: Number.NaN };
            this._pointer = { page: nap, screen: nap, client: nap };
            const canvas = evt.srcElement;
            const stage = GETVAR(canvas, CANVAS_SCENE_VAR);
            this._requestDraw(stage);
        };
        this._mousemoveHandler = (evt) => {
            this._scopeEvent = evt;
            this._pointer = CustomEventUtils.getEventCoordinates(evt);
            const canvas = evt.srcElement;
            const stage = GETVAR(canvas, CANVAS_SCENE_VAR);
            this._requestDraw(stage);
            if (!this._dragging) {
                if (!Utils.isNull(this._hitTarget)) {
                    AdapterUtils.stageDispatch(stage, 'move', evt);
                }
            }
        };
        this._mouseDownUpHandler = (evt) => {
            this._scopeEvent = evt;
            this._pointer = CustomEventUtils.getEventCoordinates(evt);
            const canvas = evt.target;
            if (canvas instanceof HTMLCanvasElement) {
                const stage = GETVAR(canvas, CANVAS_SCENE_VAR), hitTarget = this._hitTarget;
                const type = evt.type.replace(/^(mouse|touch)/, ''), fineType = type === 'end' ? 'click' : type;
                const opts = getStageOptions(stage);
                if (CustomEventUtils.matchModifiers(evt, opts.clickModifiers)) {
                    if (!Utils.isNull(hitTarget) && hitTarget.stage === stage) {
                        AdapterUtils.itemDispatch(hitTarget, fineType, evt);
                    }
                    else {
                        AdapterUtils.stageDispatch(stage, fineType, evt);
                    }
                }
            }
        };
        this._scenes = new WeakMap();
        this._handles = new WeakMap();
    }
    snapshot(stage, backgroundColor, type, quality) {
        const scenes = this._scenes;
        if (scenes.has(stage)) {
            const ctx2d = scenes.get(stage).context, srcCanvas = ctx2d.canvas;
            return this.snapshotElement(srcCanvas, backgroundColor, type, quality);
        }
        else {
            return Promise.resolve(null);
        }
    }
    getCanvas(stage) {
        const scenes = this._scenes;
        if (scenes.has(stage)) {
            const ctx2d = scenes.get(stage).context;
            return ctx2d.canvas;
        }
        else {
            return null;
        }
    }
    _getScreenInverseMatrix(m, offset) {
        return Matrix2D.invert({ a: m.a, b: m.b, c: m.c, d: m.d, e: m.e + offset.x, f: m.f + offset.y });
    }
    getTransformMatrix(scene) {
        const scenes = this._scenes;
        if (scenes.has(scene)) {
            return scenes.get(scene).screenInverseMatrix;
        }
        return Matrix2D.identity;
    }
    #dragger;
    viewActivatedCallback() {
        super.viewActivatedCallback();
        const dragDrop = this.#dragger = document.createElement(P + '-drag-drop');
        dragDrop.mode = UI.DragDataMode.Copy;
        dragDrop.dropBehavior = UI.DropBehavior.None;
        dragDrop.spillBehavior = UI.DropTargetMissedBehavior.None;
        const floater = document.createElement('div');
        floater.hidden = true;
        dragDrop.floater = floater;
        // append
        const shell = CustomElementUtils.findAncestorShell(this);
        shell.appendChild(dragDrop);
        dragDrop.addEventListener(UI.DragDropEventType.Init, this._dragInitHandler, false);
        dragDrop.addEventListener(UI.DragDropEventType.Drag, this._draggingHandler, false);
        dragDrop.addEventListener(UI.DragDropEventType.End, this._dragEndHandler, false);
    }
    disconnectedCallback() {
        const dragger = this.#dragger;
        if (!Utils.isNull(dragger)) {
            dragger.removeEventListener(UI.DragDropEventType.Init, this._dragInitHandler, false);
            dragger.removeEventListener(UI.DragDropEventType.Drag, this._draggingHandler, false);
            dragger.removeEventListener(UI.DragDropEventType.End, this._dragEndHandler, false);
            dragger.remove();
        }
        super.disconnectedCallback();
    }
    invalidateSize(scene, size) {
        const scenes = this._scenes;
        if (!Utils.isNull(scene) && !Utils.isNullOrEmpty(size)) {
            if (scenes.has(scene)) {
                const tuple = scenes.get(scene), ctx = tuple.context;
                ctx.canvas.width = size.width;
                ctx.canvas.height = size.height;
                const offset = tuple.offset = Utils.offsetRect(ctx.canvas);
                // transform matrix
                const stage = { x: 0, y: 0, width: size.width, height: size.height }, aspectRatio = scene.aspectRatio, viewbox = scene.viewbox;
                if (AdapterUtils.isValidViewbox(viewbox)) {
                    // defaults to the equivalent of SVG's xMidYMid meet
                    let mode = 'contain', align = 'center', valign = 'middle';
                    if (typeof aspectRatio === 'object') {
                        mode = aspectRatio.slice ? 'cover' : 'contain';
                        switch (aspectRatio.x) {
                            case 'min':
                                align = 'left';
                                break;
                            case 'max':
                                align = 'right';
                                break;
                        }
                        switch (aspectRatio.y) {
                            case 'min':
                                valign = 'top';
                                break;
                            case 'max':
                                valign = 'bottom';
                                break;
                        }
                    }
                    ;
                    // mimic svg viewbox
                    const mscale = Rect.findTransform({ x: 0, y: 0, width: viewbox.width, height: viewbox.height }, stage, mode, align, valign);
                    const m = Matrix2D.translate(mscale, { x: -mscale.a * viewbox.x, y: -mscale.a * viewbox.y });
                    tuple.transformMatrix = m;
                    tuple.screenInverseMatrix = this._getScreenInverseMatrix(m, offset);
                }
                else {
                    tuple.transformMatrix = Matrix2D.identity;
                }
            }
        }
        this._requestDraw(scene);
    }
    getHitTarget(stage) {
        const target = this._hitTarget;
        if (!Utils.isNull(target) && target.stage === stage) {
            return target;
        }
        return null;
    }
    initialize(scene) {
        if (Utils.isNull(scene)) {
            throw 'Provided scene is null or undefined.';
        }
        const scenes = this._scenes;
        const dragger = this.#dragger;
        // already in the store?
        if (scenes.has(scene)) {
            const canvas = scenes.get(scene).context.canvas;
            if (!Utils.isNull(dragger)) {
                dragger.register(canvas);
            }
            return canvas;
        }
        const stage = scene.stage;
        // empty stage DOM
        stage.innerHTML = '';
        const canvas = document.createElement('canvas');
        canvas.setAttribute('part', 'stage');
        SETVAR(canvas, CANVAS_SCENE_VAR, scene);
        const context = canvas.getContext('2d');
        canvas.addEventListener('mousemove', this._mousemoveHandler, false);
        canvas.addEventListener('mouseleave', this._mouseleaveHandler, false);
        canvas.addEventListener('touchstart', this._mouseDownUpHandler, { passive: true });
        canvas.addEventListener('touchmove', this._mouseDownUpHandler, { passive: true });
        canvas.addEventListener('touchend', this._mouseDownUpHandler, { passive: true });
        canvas.addEventListener('click', this._mouseDownUpHandler, false);
        canvas.addEventListener('mousedown', this._mouseDownUpHandler, false);
        canvas.addEventListener('mouseup', this._mouseDownUpHandler, false);
        stage.appendChild(canvas);
        scenes.set(scene, { context, transformMatrix: Matrix2D.identity, offset: Utils.offsetRect(canvas), screenInverseMatrix: Matrix2D.identity });
        if (!Utils.isNull(dragger)) {
            dragger.register(canvas);
        }
        return canvas;
    }
    dispose(scene) {
        const scenes = this._scenes;
        if (scenes.has(scene)) {
            var tuple = scenes.get(scene), context = tuple.context, canvas = context.canvas;
            const dragger = this.#dragger;
            if (!Utils.isNull(dragger)) {
                dragger.unregister(canvas);
            }
            canvas.removeEventListener('click', this._mouseDownUpHandler, false);
            canvas.removeEventListener('touchend', this._mouseDownUpHandler, false);
            canvas.removeEventListener('mousedown', this._mouseDownUpHandler, false);
            canvas.removeEventListener('mouseup', this._mouseDownUpHandler, false);
            // canvas.removeEventListener('touchmove', this._mouseDownUpHandler);
            canvas.removeEventListener('touchstart', this._mouseDownUpHandler);
            canvas.removeEventListener('mouseleave', this._mouseleaveHandler, false);
            canvas.removeEventListener('mousemove', this._mousemoveHandler, false);
            // remove
            canvas.remove();
            scenes.delete(scene);
        }
    }
    _requestDraw(scene) {
        const handles = this._handles;
        if (handles.has(scene)) {
            cancelAnimationFrame(handles.get(scene));
        }
        const throttle = () => {
            this.draw(scene);
            return requestAnimationFrame(() => { });
        };
        handles.set(scene, throttle());
    }
    #canvasCssStyle;
    draw(scene) {
        const scenes = this._scenes, handles = this._handles;
        if (!Utils.isNull(scene)) {
            if (scene.adapter !== this) {
                // not a pertinent stage anymore
                if (scenes.has(scene)) {
                    scenes.delete(scene);
                    handles.get(scene);
                }
                return;
            }
            if (!scenes.has(scene)) {
                // forgiving behavior (call initialize() if it's the case)
                this.initialize(scene);
                return;
            }
            if (handles.has(scene)) {
                // already in the drawing loop, reset
                cancelAnimationFrame(handles.get(scene));
            }
            const formerHitTarget = this._hitTarget;
            const items = scene.datasource || [];
            // reset hit target
            this._hitTarget = null;
            // clear stage
            const tuple = scenes.get(scene), context = tuple.context, canvas = context.canvas;
            this.#canvasCssStyle = getComputedStyle(canvas);
            context.resetTransform();
            context.clearRect(0, 0, canvas.width, canvas.height);
            // viewbox
            const m = tuple.transformMatrix;
            context.setTransform(m);
            // hit test
            const pointer = this._pointer, offset = tuple.offset, point = Number.isNaN(pointer.page.x) || Number.isNaN(pointer.page.y) ? null : { x: pointer.page.x - offset.x, y: pointer.page.y - offset.y };
            // draw recursively
            for (let drawable of items) {
                this._draw(scene, context, drawable, { transformMatrix: m }, point);
            }
            // check hit target
            const currentHitTarget = this._hitTarget;
            if (currentHitTarget != formerHitTarget) {
                if (!Utils.isNull(formerHitTarget)) {
                    if (formerHitTarget instanceof Element) {
                        formerHitTarget.dispatchEvent(new DrawableEvent('out', formerHitTarget, this._scopeEvent, m));
                    }
                    scene.dispatchEvent(new DrawableEvent('itemout', formerHitTarget, this._scopeEvent, m));
                }
                if (!Utils.isNull(currentHitTarget)) {
                    if (currentHitTarget instanceof Element) {
                        currentHitTarget.dispatchEvent(new DrawableEvent('over', currentHitTarget, this._scopeEvent, m));
                    }
                    scene.dispatchEvent(new DrawableEvent('itemover', currentHitTarget, this._scopeEvent, m));
                }
            }
            // do not loop
            // this._requestDraw(scene);
        }
    }
    _draw(scene, ctx, item, state, point) {
        item.stage ??= scene;
        if (isDrawable(item)) {
            if (item.hide) {
                return;
            }
        }
        // store parent matrix value for dragging purposes
        if (isUiObject(item) && item.draggable && !item.hide && !item.inert) {
            SETVAR(item, PARENT_MATRIX_VAR, ctx.getTransform().inverse());
        }
        let t;
        if (isUiObject(item)) {
            t = item.transformMatrix;
            if (!Utils.isNull(t) && !Matrix2D.isIdentity(t)) {
                ctx.transform(t.a, t.b, t.c, t.d, t.e, t.f);
            }
        }
        let contextPresentationState = state;
        if (isPresentationObject(item)) {
            //const stateWithWorldTransformMatrix = Utils.extend({}, state, { transformMatrix: ctx.getTransform() });
            contextPresentationState = PresentationState.combine(item, state, ctx.getTransform());
            // set presentation state
            this._setPresentationState(ctx, contextPresentationState);
        }
        if (isShape(item) || item instanceof ShapeElement) {
            this._drawShape(ctx, item, point);
        }
        else if (isText(item) || item instanceof PacemTextElement) {
            this._drawText(ctx, item, point);
        }
        else if (isImage(item) || item instanceof PacemImageElement) {
            this._drawImage(ctx, item, point);
        }
        else if (isGroup(item) || item instanceof PacemGroupElement) {
            for (let child of item.childDrawables || []) {
                this._draw(scene, ctx, child, contextPresentationState, point);
            }
        }
        // store global matrix value for debugging purposes
        if (isUiObject(item) && item.draggable && !item.hide && !item.inert) {
            SETVAR(item, WORLD_MATRIX_VAR, ctx.getTransform().inverse());
        }
        // reset presentation state
        this._setPresentationState(ctx, state);
    }
    _setPresentationState(ctx, item) {
        ctx.setTransform(item.transformMatrix);
        if (!Utils.isNullOrEmpty(item.stroke) && !isNone(item.stroke)) {
            ctx.strokeStyle = item.stroke;
        }
        else {
            ctx.strokeStyle = 'transparent';
        }
        if (!Utils.isNullOrEmpty(item.lineWidth)) {
            ctx.lineWidth = item.lineWidth;
        }
        else {
            ctx.lineWidth = 0;
        }
        if (!Utils.isNullOrEmpty(item.dashArray)) {
            ctx.setLineDash(item.dashArray);
        }
        else {
            ctx.setLineDash([]);
        }
        if (!Utils.isNullOrEmpty(item.lineCap)) {
            ctx.lineCap = item.lineCap;
        }
        else {
            ctx.lineCap = 'butt';
        }
        if (!Utils.isNullOrEmpty(item.lineJoin)) {
            ctx.lineJoin = item.lineJoin;
        }
        else {
            ctx.lineJoin = 'miter';
        }
        if (typeof item.fill === 'object' && !Utils.isNullOrEmpty(item.fill)) {
            const { stops } = item.fill;
            let gradient;
            if (isLinearGradient(item.fill)) {
                // linear gradient
                const { start, end } = item.fill;
                gradient = ctx.createLinearGradient(start.x, start.y, end.x, end.y);
            }
            else if (isRadialGradient(item.fill)) {
                // radial gradient
                const { center, radius } = item.fill;
                gradient = ctx.createRadialGradient(center.x, center.y, radius, center.x, center.y, radius);
            }
            else {
                throw new Error('Unmanaged gradient type.');
            }
            for (let stop of stops) {
                gradient.addColorStop(stop.offset, stop.color);
            }
            ctx.fillStyle = gradient;
        }
        else if (!Utils.isNullOrEmpty(item.fill) && typeof item.fill === 'string' && !isNone(item.fill)) {
            ctx.fillStyle = item.fill;
        }
        else {
            ctx.fillStyle = 'transparent';
        }
        if (!Utils.isNullOrEmpty(item.opacity)) {
            ctx.globalAlpha = item.opacity;
        }
        else {
            ctx.globalAlpha = 1.0;
        }
    }
    _drawImage(ctx, item, point) {
        const CANVAS_IMAGESOURCE_VAR = 'pacem:2d-canvas-imagesrc';
        let img = GETVAR(item, CANVAS_IMAGESOURCE_VAR);
        const imgLoadHandler = () => {
            let dw = img.naturalWidth, dh = img.naturalHeight, w = item.width, h = item.height;
            if (w > 0 && h > 0) {
            }
            else if (w > 0) {
                h = dh * w / dw;
            }
            else if (h > 0) {
                w = dw * h / dh;
            }
            else {
                w = dw;
                h = dh;
            }
            if (!this._dragging
                && !item.inert /* is hit-test visible? */
                && !Utils.isNull(point)) {
                const path2D = new Path2D(`M ${item.x} ${item.y} h ${w} v ${h} H ${item.x} Z`);
                if (ctx.isPointInPath(path2D, point.x, point.y)) {
                    // overwrite current hit-target (last one wins)
                    this._hitTarget = item;
                }
            }
            ctx.globalAlpha = fallback(item.opacity, 1);
            ctx.drawImage(img, item.x, item.y, w, h);
        };
        if (Utils.isNull(img)) {
            img = new Image();
            img.src = item.src;
            img.onload = () => {
                SETVAR(item, CANVAS_IMAGESOURCE_VAR, img);
                imgLoadHandler();
            };
        }
        else {
            imgLoadHandler();
        }
    }
    _drawText(ctx, item, point) {
        const defaults = this.DefaultShapeValues;
        const color = ctx.fillStyle = fallback(item.color, defaults.stroke);
        const m = ctx.getTransform(), fontSize = item.fontSize > 0 ? `${item.fontSize}px` : this.#canvasCssStyle.fontSize, fontStyle = item.fontStyle || this.#canvasCssStyle.fontStyle, fontWeight = item.fontWeight || this.#canvasCssStyle.fontWeight;
        ctx.font = `${fontStyle} ${fontWeight} ${fontSize} ${(item.fontFamily ?? this.#canvasCssStyle.fontFamily)}`;
        ctx.textAlign = item.textAnchor === 'middle' ? 'center' : item.textAnchor;
        ctx.fillText(item.text, item.anchor.x, item.anchor.y);
        ctx.globalAlpha = fallback(item.opacity, 1);
        const hasColor = !isNone(color);
        if (!this._dragging
            && !item.inert /* is hit-test visible? */
            && !Utils.isNull(point)) {
            const bbox = ctx.measureText(item.text);
            const width = bbox.width, height = bbox.actualBoundingBoxAscent + bbox.actualBoundingBoxDescent, x = item.anchor.x - bbox.actualBoundingBoxLeft, y = item.anchor.y - bbox.actualBoundingBoxAscent;
            const path2D = new Path2D(`M ${x} ${y} h ${width} v ${height} H ${x} z`);
            if (hasColor && ctx.isPointInPath(path2D, point.x, point.y)) {
                // overwrite current hit-target (last one wins)
                this._hitTarget = item;
            }
        }
    }
    _drawShape(ctx, item, point) {
        if (Utils.isNullOrEmpty(item?.pathData)) {
            return;
        }
        ctx.beginPath();
        // Path2D
        const hasFill = typeof ctx.fillStyle !== 'string' || !isNone(ctx.fillStyle), hasStroke = typeof ctx.strokeStyle !== 'string' || !isNone(ctx.strokeStyle);
        var path2D = new Path2D(item.pathData);
        if (!this._dragging
            && !item.inert /* is hit-test visible? */
            && !Utils.isNull(point)) {
            if ((hasFill && ctx.isPointInPath(path2D, point.x, point.y))
                || (hasStroke && ctx.isPointInStroke(path2D, point.x, point.y))) {
                // overwrite current hit-target (last one wins)
                this._hitTarget = item;
            }
        }
        if (hasStroke) {
            ctx.stroke(path2D);
        }
        if (hasFill) {
            ctx.fill(path2D);
        }
        const hasEnoughVertices = !Utils.isNullOrEmpty(item.vertices) && item.vertices.length > 1, hasMarkerEnd = !Utils.isNull(item.markerEnd), hasMarkerStart = !Utils.isNull(item.markerStart), hasMarkerMid = !Utils.isNull(item.markerMid);
        if (hasMarkerEnd || hasMarkerMid || hasMarkerStart) {
            if (!hasEnoughVertices) {
                this.log(Logging.LogLevel.Warn, "Not enough vertices were explicited in order to make markers renderable.");
            }
        }
        // WebGL: we can render markers only if vertices are explicit
        if (hasEnoughVertices) {
            const matrix = ctx.getTransform();
            if (hasMarkerStart) {
                const marker = item.markerEnd, from = item.vertices[1], to = item.vertices[0];
                const orientation = Math.atan2(to.y - from.y, to.x - from.x);
                this._drawMarker(ctx, marker, to, orientation);
            }
            if (hasMarkerMid) {
                const marker = item.markerEnd;
                for (let j = 1; j < item.vertices.length - 1; j++) {
                    // reset transform, just in case
                    ctx.setTransform(matrix);
                    const from = item.vertices[j], to = item.vertices[j + 1];
                    const orientation = Math.atan2(to.y - from.y, to.x - from.x);
                    this._drawMarker(ctx, marker, to, orientation);
                }
            }
            if (hasMarkerEnd) {
                // reset transform, just in case
                ctx.setTransform(matrix);
                const marker = item.markerEnd, from = item.vertices[item.vertices.length - 2], to = item.vertices[item.vertices.length - 1];
                const orientation = Math.atan2(to.y - from.y, to.x - from.x);
                this._drawMarker(ctx, marker, to, orientation);
            }
        }
    }
    _drawMarker(ctx, marker, point, orientation) {
        const defaults = this.DefaultShapeValues;
        const refX = marker.ref?.x || 0, refY = marker.ref?.y || 0;
        const width = (marker.width || 1) * ctx.lineWidth, height = (marker.width || 1) * ctx.lineWidth;
        let viewbox = marker.viewbox, stage = { x: 0, y: 0, width: width, height: height };
        if (Utils.isNull(viewbox)) {
            viewbox = stage;
        }
        // prepare
        const mscale = { a: stage.width / viewbox.width, d: stage.height / viewbox.height }; // Rect.findTransform({ x: 0, y: 0, width: viewbox.width, height: viewbox.height }, stage, 'stretch', 'left', 'top');
        // execute
        ctx.translate(point.x, point.y);
        ctx.rotate(orientation);
        ctx.scale(mscale.a, mscale.d);
        ctx.translate(-refX, -refY);
        ctx.fillStyle = fallback(marker.fill, defaults.fill);
        ctx.strokeStyle = fallback(marker.stroke, defaults.stroke);
        // Path2D
        const hasMarkerFill = !isNone(marker.fill), hasMarkerStroke = !isNone(marker.stroke);
        var markerPath2D = new Path2D(marker.pathData);
        if (hasMarkerFill) {
            ctx.fill(markerPath2D);
        }
        if (hasMarkerStroke) {
            ctx.stroke(markerPath2D);
        }
    }
};
PacemCanvasAdapterElement = __decorate([
    CustomElement({ tagName: P + '-' + TAG_MIDDLE_NAME + '-canvas-adapter' })
], PacemCanvasAdapterElement);
export { PacemCanvasAdapterElement };
